import { useMemo, useState } from 'react'
import { useMqttTopic } from '../mqtt.jsx'
import { useSelection } from '../selection.jsx'
import { sortSessionCodes, sessionLabel, sessionIsScored, isRunPB } from '../sessions.js'
import { fmtSessionTime, riderMatchesQuery, gateColumns, gateValueFor, splitRiderName } from '../format.js'
import { sortClasses } from '../classes.js'

const EMPTY_CLASSES = []

export default function ResultsPage() {
  const results = useMqttTopic('results')
  const venue = useMqttTopic('venue')
  const sessionOverrides = useMqttTopic('sessions')?.overrides
  const { selected, toggle } = useSelection()

  const [pickedSession, setPickedSession] = useState(null)
  const [classFilter, setClassFilter] = useState(null)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState({ key: null, dir: 1 })

  const classes = results?.classes ?? EMPTY_CLASSES

  const sessionCodes = useMemo(() => {
    const codes = new Set()
    classes.forEach((c) => c.competitors.forEach((r) => Object.keys(r.sessions || {}).forEach((code) => codes.add(code))))
    return sortSessionCodes([...codes])
  }, [classes])

  // Follow the newest session as the event progresses, unless the viewer
  // has explicitly picked one that's still present - derived on every
  // render rather than synced via an effect, so a stale pick falls back to
  // "newest" automatically instead of needing to be reset from outside.
  const session =
    pickedSession && sessionCodes.includes(pickedSession) ? pickedSession : sessionCodes[sessionCodes.length - 1]

  if (!results) {
    return <div className="empty-state">Waiting for results…</div>
  }

  const orderedClassNames = sortClasses(classes.map((c) => c.className))
  const byClassName = Object.fromEntries(classes.map((c) => [c.className, c]))

  return (
    <div>
      <div className="toolbar">
        <div className="session-switch">
          {sessionCodes.map((code) => (
            <button
              key={code}
              className={`chip ${sessionIsScored(code, sessionOverrides) ? 'timed' : ''} ${session === code ? 'active' : ''}`}
              onClick={() => setPickedSession(code)}
            >
              <span className="code">{code}</span> {sessionLabel(code, sessionOverrides)}
            </button>
          ))}
        </div>
        <div className="spacer" />
        <div className="search">
          <span aria-hidden="true">⌕</span>
          <input
            type="text"
            placeholder="Filter by rider, machine or no.…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="class-filters">
        <button className={`chip ${!classFilter ? 'active' : ''}`} onClick={() => setClassFilter(null)}>
          All
        </button>
        {orderedClassNames.map((cls) => (
          <button key={cls} className={`chip ${classFilter === cls ? 'active' : ''}`} onClick={() => setClassFilter(cls)}>
            {cls}
          </button>
        ))}
      </div>

      <div className="legend">
        <span>
          <span className="swatch purple" /> Fastest in this class for the selected session
        </span>
        <span>
          <span className="swatch red" /> Flagged
        </span>
      </div>

      {orderedClassNames
        .filter((cls) => !classFilter || cls === classFilter)
        .map((cls) => {
          const cdata = byClassName[cls]
          let rows = cdata.competitors.filter((r) => riderMatchesQuery(r, search))
          if (rows.length === 0) return null

          if (sort.key) {
            rows = [...rows].sort((a, b) => {
              let av, bv
              if (sort.key === 'rider') {
                av = a.rider
                bv = b.rider
              } else if (sort.key === 'num') {
                av = a.num
                bv = b.num
              } else {
                av = a.sessions?.[session]?.time ?? null
                bv = b.sessions?.[session]?.time ?? null
              }
              if (av == null) return 1
              if (bv == null) return -1
              if (av < bv) return -1 * sort.dir
              if (av > bv) return 1 * sort.dir
              return 0
            })
          }

          const best = rows.reduce((min, r) => {
            const t = r.sessions?.[session]?.time
            return t && (min == null || t < min) ? t : min
          }, null)

          // Gate columns are additive - a class with no gate data (or a
          // provider that never sent any) just gets none, no layout change.
          // One column per distinct gate *field*, not position - see
          // gateColumns()'s own comment for why that distinction matters.
          const cols = gateColumns(rows, session, venue?.splits)
          // Times (type T) are best when lowest; speed-trap readings
          // (type S) are best when highest - a speed isn't "faster" for
          // being a smaller number.
          const bestAtField = Object.fromEntries(
            cols.map((c) => [
              c.field,
              rows.reduce((best, r) => {
                const v = gateValueFor(r.sessions?.[session], c.field)
                if (v == null) return best
                if (best == null) return v
                return c.type === 'S' ? Math.max(best, v) : Math.min(best, v)
              }, null),
            ]),
          )

          const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }))

          // table-layout: fixed is only content-independent once the table
          // has a *definite* width - browsers still fall back to measuring
          // real cell content (a long rider name, a PB pill) to work out
          // the table's own shrink-to-fit size, even under fixed layout,
          // which was silently inflating every column past the CSS widths
          // below no matter how they were set (confirmed via a temporary
          // debug overlay reading getBoundingClientRect on each header
          // cell - e.g. the 30px checkbox column was rendering at 47px).
          // Giving it an explicit pixel width - the same numbers as the
          // mobile column widths in index.css - forces genuine
          // content-independent sizing. Kept as a CSS custom property
          // rather than a `width` prop so desktop's own separate
          // min-width: 600px rule (index.css) is untouched.
          const mobileTableWidth = 30 + 34 + 44 + 160 + cols.length * 64 + 64

          return (
            <div className="class-section" key={cls}>
              <h3>{cls}</h3>
              <div className="table-wrap">
                <table style={{ '--mobile-table-width': `${mobileTableWidth}px` }}>
                  <thead>
                    <tr>
                      <th style={{ width: 30 }} />
                      <th className="num-col pos-col">Pos</th>
                      <th className={`num-col bib-col ${sort.key === 'num' ? 'sorted' : ''}`} onClick={() => toggleSort('num')}>
                        # <span className="arrow">{sort.key === 'num' ? (sort.dir === 1 ? '↑' : '↓') : '↑↓'}</span>
                      </th>
                      <th className={`rider-col ${sort.key === 'rider' ? 'sorted' : ''}`} onClick={() => toggleSort('rider')}>
                        Rider <span className="arrow">{sort.key === 'rider' ? (sort.dir === 1 ? '↑' : '↓') : '↑↓'}</span>
                      </th>
                      {cols.map((c) => (
                        <th key={c.field} className="num-col">
                          {c.label}
                        </th>
                      ))}
                      <th className={`num-col ${sort.key === 'time' ? 'sorted' : ''}`} onClick={() => toggleSort('time')}>
                        Time <span className="arrow">{sort.key === 'time' ? (sort.dir === 1 ? '↑' : '↓') : '↑↓'}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const t = fmtSessionTime(r.sessions?.[session])
                      const isBest = t.value != null && best != null && t.value <= best + 0.001
                      const [driverName, passengerName] = splitRiderName(r.rider)
                      return (
                        <tr key={r.num} className={selected.has(r.num) ? 'selected' : ''}>
                          <td>
                            <input type="checkbox" className="check" checked={selected.has(r.num)} onChange={() => toggle(r.num)} />
                          </td>
                          <td className="pos">{r.pos || '—'}</td>
                          <td className="num-col mono">{r.num}</td>
                          <td className="rider">
                            <span className="name">{driverName}</span>
                            {passengerName && <span className="passenger">{passengerName}</span>}
                            <span className="machine">{r.machine}</span>
                          </td>
                          {cols.map((c) => {
                            const v = gateValueFor(r.sessions?.[session], c.field)
                            const bestForField = bestAtField[c.field]
                            const isBestGate =
                              v != null &&
                              bestForField != null &&
                              (c.type === 'S' ? v >= bestForField - 0.001 : v <= bestForField + 0.001)
                            return (
                              <td key={c.field} className={`split num-col ${isBestGate ? 'purple' : ''}`}>
                                {/* Speed traps (1dp) are the only gate type that isn't a
                                    time - NIGAN's own Finish-beam gate (type 'F', see
                                    LocalStore.vb's BuildGateList) duplicates the run's
                                    finish time and must render like one (2dp), not like a
                                    speed reading - checking `=== 'T'` treated anything
                                    that wasn't exactly 'T' (including 'F') as 1dp. */}
                                {v != null ? v.toFixed(c.type === 'S' ? 1 : 2) : '·'}
                              </td>
                            )
                          })}
                          <td className={`split num-col ${isBest ? 'purple' : ''} ${t.flagged ? 'flagged' : ''}`}>
                            {t.text}
                            {isRunPB(r.sessions, session, t.value, r.personalBest) && (
                              <span className="pb-pill">PB</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )
        })}
    </div>
  )
}
